import { supabase } from './supabase';
import { toast } from 'react-hot-toast';

const OAUTH_CONFIG = {
  'meta-ads': {
    clientId: import.meta.env.VITE_META_ADS_CLIENT_ID,
    clientSecret: import.meta.env.VITE_META_ADS_CLIENT_SECRET,
    authUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
    tokenUrl: 'https://graph.facebook.com/v18.0/oauth/access_token',
    scope: [
      'ads_read',
      'ads_management',
      'business_management',
      'read_insights',
      'pages_show_list',
      'pages_read_engagement',
      'public_profile',
      'email'
    ].join(','),
    redirectUri: window.location.hostname === 'localhost' 
      ? `${window.location.origin}/#/dashboard/integrations/callback`
      : 'https://unifyads.io/#/dashboard/integrations/callback'
  }
} as const;

export type PlatformId = keyof typeof OAUTH_CONFIG;

export const initiatePlatformAuth = async (platformId: PlatformId) => {
  try {
    console.log('Initiating Meta Ads authentication...');
    const config = OAUTH_CONFIG[platformId];
    if (!config) {
      throw new Error('Platform not configured');
    }

    // Validate environment variables
    if (!config.clientId || !config.clientSecret) {
      console.error('Missing Meta Ads credentials:', { 
        hasClientId: !!config.clientId,
        hasClientSecret: !!config.clientSecret 
      });
      throw new Error('Meta Ads integration is not properly configured');
    }

    // Get user session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('User not authenticated');
    }

    // Check if user already has an active connection
    const { data: existingConnection } = await supabase
      .from('platform_connections')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('platform_id', platformId)
      .eq('is_active', true)
      .single();

    if (existingConnection) {
      throw new Error('You already have an active Meta Ads account connected. Please disconnect it first before connecting a different account.');
    }

    // Generate state
    const state = crypto.randomUUID();

    // Store state and platform info
    const { error: stateError } = await supabase
      .from('platform_oauth_states')
      .insert({
        user_id: session.user.id,
        platform_id: platformId,
        state,
        credentials: {
          client_id: config.clientId,
          client_secret: config.clientSecret,
          redirect_uri: config.redirectUri,
          scope: config.scope
        }
      });

    if (stateError) {
      console.error('Failed to store OAuth state:', stateError);
      throw stateError;
    }

    // Build OAuth URL with account selection forced
    const params = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      response_type: 'code',
      scope: config.scope,
      state,
      auth_type: 'rerequest', // Force re-request of permissions
      display: 'popup'
    });

    const authUrl = `${config.authUrl}?${params.toString()}`;
    console.log('Redirecting to Meta Ads auth URL:', authUrl);
    window.location.href = authUrl;
  } catch (error) {
    console.error('Meta Ads auth error:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to connect Meta Ads');
  }
};

export const handleAuthCallback = async (params: URLSearchParams) => {
  try {
    console.log('Processing Meta Ads callback...');
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error) {
      console.error('OAuth error response:', { error, errorDescription });
      throw new Error(errorDescription || error);
    }

    if (!code || !state) {
      console.error('Missing required parameters:', { code, state });
      throw new Error('Missing required parameters');
    }

    // Verify state and get stored data
    const { data: stateData, error: stateError } = await supabase
      .from('platform_oauth_states')
      .select('user_id, platform_id, credentials')
      .eq('state', state)
      .single();

    if (stateError || !stateData) {
      console.error('State verification failed:', { stateError, stateData });
      throw new Error('Invalid state parameter');
    }

    // Exchange code for access token
    console.log('Exchanging code for access token...');
    const tokenResponse = await fetch('https://graph.facebook.com/v18.0/oauth/access_token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: stateData.credentials.client_id,
        client_secret: stateData.credentials.client_secret,
        redirect_uri: stateData.credentials.redirect_uri,
        code,
      }).toString(),
    });

    if (!tokenResponse.ok) {
      const errorData = await tokenResponse.json();
      console.error('Token exchange failed:', errorData);
      throw new Error(errorData.error?.message || 'Failed to exchange code for access token');
    }

    const tokenData = await tokenResponse.json();
    console.log('Successfully obtained access token');

    // Calculate expiration time
    const now = new Date();
    const expiresAt = new Date(now.getTime() + (60 * 24 * 60 * 60 * 1000)); // 60 days

    // Get ad accounts
    console.log('Fetching ad accounts...');
    const accountsResponse = await fetch(
      'https://graph.facebook.com/v18.0/me/adaccounts?' +
      'fields=account_id,name,business_name,account_status,currency,timezone_name',
      {
        headers: { 'Authorization': `Bearer ${tokenData.access_token}` }
      }
    );

    if (!accountsResponse.ok) {
      const accountError = await accountsResponse.json();
      console.error('Failed to fetch ad accounts:', accountError);
      throw new Error('Failed to access ad accounts. Please ensure you have granted all required permissions.');
    }

    const accountsData = await accountsResponse.json();
    
    if (!accountsData.data?.length) {
      throw new Error('No Meta Ads accounts found. Please make sure you have access to an Ads account.');
    }

    // Use the first account by default
    const selectedAccount = accountsData.data[0];

    // Store the connection with the selected account
    const { error: connectionError } = await supabase
      .from('platform_connections')
      .upsert({
        user_id: stateData.user_id,
        platform_id: stateData.platform_id,
        platform_type: 'advertising',
        credentials: {
          access_token: tokenData.access_token,
          expires_at: expiresAt.toISOString(),
          account_id: selectedAccount.account_id,
          account_name: selectedAccount.name,
          business_name: selectedAccount.business_name,
          account_status: selectedAccount.account_status,
          currency: selectedAccount.currency,
          timezone: selectedAccount.timezone_name,
          available_accounts: [selectedAccount] // Store only the selected account
        },
        is_active: true,
        connected_at: new Date().toISOString(),
        last_sync: new Date().toISOString(),
      }, {
        onConflict: 'user_id,platform_id',
      });

    if (connectionError) {
      console.error('Failed to store connection:', connectionError);
      throw connectionError;
    }

    // Clean up state
    await supabase
      .from('platform_oauth_states')
      .delete()
      .eq('state', state);

    return { success: true };
  } catch (error) {
    console.error('Callback error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to complete connection'
    };
  }
};

export const getMetaAdsToken = async (userId: string): Promise<string | null> => {
  try {
    const { data: connection, error: connectionError } = await supabase
      .from('platform_connections')
      .select('credentials')
      .eq('user_id', userId)
      .eq('platform_id', 'meta-ads')
      .eq('is_active', true)
      .single();

    if (connectionError) throw connectionError;
    if (!connection?.credentials?.access_token) {
      throw new Error('No access token found');
    }

    // Check if token is expired or will expire in the next hour
    const expiresAt = new Date(connection.credentials.expires_at);
    const now = new Date();
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);

    if (expiresAt <= oneHourFromNow) {
      console.log('Token expired or expiring soon, refreshing...');
      // Token is expired or will expire soon, refresh it
      const refreshResponse = await fetch(
        'https://graph.facebook.com/v18.0/oauth/access_token?' +
        new URLSearchParams({
          grant_type: 'fb_exchange_token',
          client_id: OAUTH_CONFIG['meta-ads'].clientId,
          client_secret: OAUTH_CONFIG['meta-ads'].clientSecret,
          fb_exchange_token: connection.credentials.access_token,
        }).toString()
      );

      if (!refreshResponse.ok) {
        const errorData = await refreshResponse.json();
        console.error('Token refresh failed:', errorData);
        throw new Error('Failed to refresh access token');
      }

      const tokenData = await refreshResponse.json();
      const newExpiresAt = new Date(now.getTime() + (60 * 24 * 60 * 60 * 1000)); // 60 days

      // Update stored token
      const { error: updateError } = await supabase
        .from('platform_connections')
        .update({
          credentials: {
            ...connection.credentials,
            access_token: tokenData.access_token,
            expires_at: newExpiresAt.toISOString(),
          }
        })
        .eq('user_id', userId)
        .eq('platform_id', 'meta-ads');

      if (updateError) throw updateError;

      return tokenData.access_token;
    }

    return connection.credentials.access_token;
  } catch (error) {
    console.error('Error getting Meta Ads token:', error);
    return null;
  }
};